// src/components/RTMPNetworkAnalyticsCard.tsx
import React from 'react';
import { Paper, Typography, List, ListItem, Divider, Box } from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed'; // New Icon
import { type StreamAnalytics, type StreamStats } from '../services/streamsService';

const formatDuration = (totalSeconds: number): string => {
  if (isNaN(totalSeconds) || totalSeconds < 0) return '00:00:00';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const pad = (num: number) => num.toString().padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

interface Props {
  analytics: StreamAnalytics;
  stats: StreamStats;
  isOnline: boolean;
}

const StatItem: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <ListItem sx={{ py: 0.5, px: 0, display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" color="text.primary">{value ?? 'N/A'}</Typography>
  </ListItem>
);

export const RTMPNetworkAnalyticsCard: React.FC<Props> = ({ analytics, stats, isOnline }) => {
  const formattedDuration = formatDuration(analytics.uptimeSec);
  const formattedBitrate = analytics.bitrateKbps ? `${analytics.bitrateKbps.toFixed(0)} kbps` : '0';
  const startTime = analytics.startTime ? new Date(analytics.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A';
  const statusColor = isOnline ? 'success.main' : 'error.main';

  return (
    <Paper 
      elevation={3} 
      sx={{ borderRadius: 0, p: 2, borderLeft: `4px solid`, borderColor: statusColor, transition: 'border-color 0.5s ease-in-out' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <SpeedIcon sx={{ fontSize: '1.25rem', mr: 1, color: 'success.main' }} />
        <Typography variant="h5">RTMP Analytics</Typography>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <List dense>
        <StatItem label="Viewers" value={analytics.viewers} />
        <StatItem label="Bitrate" value={formattedBitrate} />
        <StatItem label="Start Time" value={startTime} />
        <StatItem label="Duration" value={formattedDuration} />
        <StatItem label="Remote Address" value={stats.remoteAddr} />
      </List>
    </Paper>
  );
};